import React, { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import Badge from 'assets/salesNew/offer/badge.svg';
import DateIcon from 'assets/salesNew/offer/date.svg';
import LevinIcon from 'assets/salesNew/offer/levin.svg';
import StarIcon from 'assets/salesNew/offer/star.svg';
import Modal from 'common-ui/modal';
import { addLeadOffer, getOffers, getProducts } from 'features/newsales/newsalesActions';
import LeadSourceImage from 'pages/settings/salesNewSettings/components/LeadSourceImage';

import {
  BackButton,
  BadgeIcon,
  ButtonRow,
  CancelButton,
  CardHeader,
  DateImg,
  EmptyMessage,
  ErrorMessage,
  FooterText,
  LevinImg,
  LoadingMessage,
  ModalContent,
  NextButton,
  OfferCard,
  OfferGrid,
  OfferImageWrapper,
  PriceBox,
  PriceTitle,
  PriceValue,
  ProductCard,
  ProductGrid,
  ProductImage,
  ProductName,
  ProductService,
  StarImg,
  StepContent,
  StepTitle,
  SubmitButton,
  UnitsInfo,
  ValidityBox,
} from './AddOfferModal.styles';

const STEP = {
  PRODUCT: 1,
  OFFER: 2,
};

const AddOfferModal = ({ isOpen, onClose, leadData, onSuccess }) => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(STEP.PRODUCT);
  const [products, setProducts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [selectedOfferIds, setSelectedOfferIds] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingOffers, setIsLoadingOffers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(STEP.PRODUCT);
      setSelectedProductIds([]);
      setSelectedOfferIds([]);
      setError('');
      fetchProducts();
    }
  }, [isOpen]);

  const fetchProducts = async () => {
    setIsLoadingProducts(true);
    setError('');
    try {
      const response = await dispatch(getProducts({ limit: 100 })).unwrap();
      const productsList = Array.isArray(response) ? response : response?.data || [];
      setProducts(productsList);
    } catch (err) {
      setError('Failed to load products');
      console.error('Failed to fetch products:', err);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const fetchOffers = async () => {
    setIsLoadingOffers(true);
    setError('');
    try {
      // Fetch all offers with pagination (limit 12 per page)
      let allOffers = [];
      let page = 1;
      const limit = 12;
      let hasMore = true;

      while (hasMore) {
        const params = {
          page,
          limit,
          productId: selectedProductIds,
        };
        // if (selectedProductIds.length > 0) {
        //   params.productId = selectedProductIds.join(',');
        // }

        const response = await dispatch(getOffers(params)).unwrap();
        const responseData = Array.isArray(response) ? response : response?.data || [];
        allOffers = [...allOffers, ...responseData];

        hasMore = responseData.length === limit;
        page += 1;
      }

      setOffers(allOffers);
    } catch (err) {
      setError('Failed to load offers');
      console.error('Failed to fetch offers:', err);
    } finally {
      setIsLoadingOffers(false);
    }
  };

  const handleProductToggle = (productId) => {
    setSelectedProductIds((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId);
      }
      return [...prev, productId];
    });
  };

  const handleOfferToggle = (offerId) => {
    setSelectedOfferIds((prev) => {
      if (prev.includes(offerId)) {
        return prev.filter((id) => id !== offerId);
      }
      return [...prev, offerId];
    });
  };

  const handleNext = () => {
    if (selectedProductIds.length === 0) {
      setError('Please select at least one product');
      return;
    }
    setCurrentStep(STEP.OFFER);
    fetchOffers();
  };

  const handleBack = () => {
    setCurrentStep(STEP.PRODUCT);
    setSelectedOfferIds([]);
    setError('');
  };

  const handleSubmit = async () => {
    if (selectedOfferIds.length === 0) {
      setError('Please select at least one offer');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await dispatch(
        addLeadOffer({ leadId: leadData.id, offerIds: selectedOfferIds })
      ).unwrap();

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (err) {
      console.error('Failed to add offers to lead:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={currentStep === STEP.PRODUCT ? 'Choose Product' : 'Choose Offer'}
      footer={false}
      width="1200px"
      height={'auto'}
      closeIcon={true}
    >
      <ModalContent>
        {currentStep === STEP.PRODUCT && (
          <StepContent>
            <StepTitle>Select one or more products</StepTitle>

            {isLoadingProducts ? (
              <LoadingMessage>Loading products...</LoadingMessage>
            ) : products?.length === 0 ? (
              <EmptyMessage>No products available</EmptyMessage>
            ) : (
              <ProductGrid>
                {products.map((product, index) => {
                  return (
                    <ProductCard
                      key={product.id}
                      $selected={selectedProductIds.includes(product.id)}
                      $columnIndex={index % 5}
                      onClick={() => handleProductToggle(product.id)}
                    >
                      <ProductImage>
                        <LeadSourceImage
                          imagePath={product.image}
                          alt={product.name}
                          size={64}
                        />
                      </ProductImage>
                      <ProductName $columnIndex={index % 5}>{product.name}</ProductName>
                      {product.description && (
                        <ProductService $columnIndex={index % 5}>
                          {product.description}
                        </ProductService>
                      )}
                    </ProductCard>
                  );
                })}
              </ProductGrid>
            )}

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <ButtonRow>
              <CancelButton onClick={onClose}>Back</CancelButton>
              <NextButton onClick={handleNext} disabled={selectedProductIds.length === 0}>
                Next
              </NextButton>
            </ButtonRow>
          </StepContent>
        )}

        {currentStep === STEP.OFFER && (
          <StepContent>
            <StepTitle>Select one or more offers</StepTitle>

            {isLoadingOffers ? (
              <LoadingMessage>Loading offers...</LoadingMessage>
            ) : offers?.length === 0 ? (
              <EmptyMessage>No offers available for selected products</EmptyMessage>
            ) : (
              <OfferGrid>
                {offers.map((offer) => {
                  const formatDate = (dateString) => {
                    if (!dateString) return 'N/A';
                    const date = new Date(dateString);
                    return date.toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    });
                  };

                  return (
                    <OfferCard
                      key={offer.id}
                      isActive={selectedOfferIds.includes(offer.id)}
                      onClick={() => handleOfferToggle(offer.id)}
                    >
                      <BadgeIcon src={Badge} />
                      {offer.image && (
                        <OfferImageWrapper>
                          <LeadSourceImage
                            imagePath={offer.image}
                            alt={offer.name}
                            size={30}
                          />
                        </OfferImageWrapper>
                      )}
                      <CardHeader title={offer?.name || offer.name}>
                        {offer?.name || offer.name}
                      </CardHeader>

                      <PriceBox>
                        <PriceTitle>Total Price</PriceTitle>
                        <PriceValue>
                          {offer.price || '200'} <span>{offer.currency || 'AMD'}</span>
                        </PriceValue>
                        {offer.count && (
                          <UnitsInfo>
                            <StarImg src={StarIcon} /> {offer.count} Units Included
                          </UnitsInfo>
                        )}
                      </PriceBox>

                      {(offer.validFrom || offer.validTo) && (
                        <ValidityBox>
                          <DateImg src={DateIcon} className="calendar-icon" />
                          Valid:{' '}
                          <span>
                            {formatDate(offer.validFrom)} - {formatDate(offer.validTo)}
                          </span>
                        </ValidityBox>
                      )}

                      <FooterText>
                        <LevinImg src={LevinIcon} /> Premium Offer
                      </FooterText>
                    </OfferCard>
                  );
                })}
              </OfferGrid>
            )}

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <ButtonRow>
              <BackButton onClick={handleBack}>Back</BackButton>
              <SubmitButton
                onClick={handleSubmit}
                disabled={isSubmitting || selectedOfferIds.length === 0}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </SubmitButton>
            </ButtonRow>
          </StepContent>
        )}
      </ModalContent>
    </Modal>
  );
};

export default AddOfferModal;
